import { cn } from "../../../utils/cn.js";
import { OverlayStackContext, OverlaySurfaceContext, getOverlayLayerZIndex, useOverlaySurfaceState } from "../overlay-layering.js";
import { ScrollArea, ScrollAreaContent, ScrollAreaScrollbar, ScrollAreaThumb, ScrollAreaViewport } from "../ScrollArea/ScrollArea.js";
import { cva } from "class-variance-authority";
import { XIcon } from "@phosphor-icons/react";
import * as React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Drawer as Drawer$1 } from "@base-ui/react/drawer";
//#region src/components/Drawer/Drawer.tsx
/**
* Task-drawer default for pages that should switch to a bottom sheet on phone
* widths: swipe-down sheet below `md`, right-side panel from `md` up.
*/
const DRAWER_SIDE_SHEET_ON_MOBILE = {
	base: "bottom",
	md: "right"
};
/**
* The resolved side of the nearest enclosing drawer, or `null` outside one.
* A nested `Drawer` without its own `side` inherits this, so a child opened
* from a bottom sheet is also a sheet and the stack recedes on one axis.
*/
const DrawerSideContext = React.createContext(null);
/**
* Lets the popup report the measured handle + header height so a bottom sheet
* can rest there. `null` clears the snap.
*/
const DrawerHeaderSnapContext = React.createContext(null);
const SIDE_TO_SWIPE_DIRECTION = {
	right: "right",
	left: "left",
	bottom: "down",
	top: "up"
};
const BREAKPOINT_ORDER = [
	"sm",
	"md",
	"lg"
];
/** Tailwind v4 defaults, used when `--breakpoint-*` is not readable. */
const BREAKPOINT_FALLBACK = {
	sm: "40rem",
	md: "48rem",
	lg: "64rem"
};
/**
* `min-width` query for a breakpoint, read from the same `--breakpoint-*`
* theme variable that powers Tailwind's `sm:` / `md:` / `lg:` so the drawer
* switches at exactly the width the page's responsive classes do.
*/
function breakpointQuery(breakpoint) {
	return `(min-width: ${(typeof document === "undefined" ? "" : getComputedStyle(document.documentElement).getPropertyValue(`--breakpoint-${breakpoint}`).trim()) || BREAKPOINT_FALLBACK[breakpoint]})`;
}
function canMatchMedia() {
	return typeof window !== "undefined" && typeof window.matchMedia === "function";
}
/**
* Resolves a static or responsive `side` to the edge for the current
* viewport, re-rendering when a listed breakpoint is crossed.
*
* Without `matchMedia` (SSR, jsdom) it resolves to the widest listed
* breakpoint's side, so server markup and component tests get the desktop
* panel rather than a sheet.
*
* @param side - A fixed side or a mobile-first breakpoint map
* @returns The side to render
*/
function useResolvedDrawerSide(side) {
	const steps = React.useMemo(() => {
		if (typeof side === "string") return null;
		return BREAKPOINT_ORDER.flatMap((breakpoint) => {
			const value = side[breakpoint];
			return value ? [{
				breakpoint,
				value
			}] : [];
		});
	}, [side]);
	const stepsKey = steps?.map((step) => `${step.breakpoint}:${step.value}`).join("|");
	const base = typeof side === "string" ? side : side.base;
	const resolve = React.useCallback((canMatch) => {
		if (!steps || steps.length === 0) return base;
		if (!canMatch) return steps[steps.length - 1].value;
		let current = base;
		for (const step of steps) if (window.matchMedia(breakpointQuery(step.breakpoint)).matches) current = step.value;
		return current;
	}, [base, stepsKey]);
	const subscribe = React.useCallback((onChange) => {
		if (!steps || steps.length === 0 || !canMatchMedia()) return () => {};
		const lists = steps.map((step) => window.matchMedia(breakpointQuery(step.breakpoint)));
		lists.forEach((list) => list.addEventListener("change", onChange));
		return () => lists.forEach((list) => list.removeEventListener("change", onChange));
	}, [stepsKey]);
	return React.useSyncExternalStore(subscribe, () => resolve(canMatchMedia()), () => resolve(false));
}
/**
* Lets a child drawer tell every ancestor drawer that it is currently open.
*
* This is separate from the shared `OverlaySurfaceContext`: that channel
* drives the generic nested-overlay tint (used by dialogs opened from a
* drawer), while this one drives the drawer-only "recede" treatment where the
* parent panel scales down and shifts away from its anchored edge so the
* stack reads as layered cards. Registrations propagate up the chain so each
* ancestor knows how many descendant drawers are open, not just its direct
* children.
*/
const DrawerStackContext = React.createContext(null);
/**
* Mirrors the Base UI root's logical `open` state for the drawer subtree.
*
* Base UI keeps the popup mounted through its exit transition, so keying the
* stack registration on mount would make a parent wait for the child's full
* 450 ms slide-out before animating forward again. Keying it on `open`
* instead lets the parent return at the same moment the child starts leaving,
* mirroring the concurrent open animation.
*/
const DrawerOpenContext = React.createContext(false);
/**
* Tracks how many descendant drawers are open beneath the current drawer and
* registers the current drawer with its ancestors while it is open.
*
* Each registration from a child is forwarded to the parent so depth
* accumulates along the chain: a grandparent sees both the child and the
* grandchild, which lets it recede two steps instead of one.
*
* @param isOpen - Whether the current drawer is logically open (not merely
*   mounted for its exit transition)
* @returns Number of open descendant drawers and a provider value for children
*/
function useDrawerStackState(isOpen) {
	const registerWithParent = React.useContext(DrawerStackContext);
	const [nestedDrawerCount, setNestedDrawerCount] = React.useState(0);
	React.useEffect(() => {
		if (!isOpen || !registerWithParent) return;
		return registerWithParent();
	}, [isOpen, registerWithParent]);
	return {
		nestedDrawerCount,
		registerNestedDrawer: React.useCallback(() => {
			setNestedDrawerCount((count) => count + 1);
			const unregisterFromParent = registerWithParent?.();
			return () => {
				setNestedDrawerCount((count) => count - 1);
				unregisterFromParent?.();
			};
		}, [registerWithParent])
	};
}
/**
* Groups all parts of the drawer. Wraps the Base UI Drawer.Root primitive
* and automatically maps the `side` prop to the correct `swipeDirection`.
*
* Also mirrors the root's `open` state into `DrawerOpenContext` so nested
* drawers can drive the parent recede from the logical open flag rather than
* from popup mount. Controlled roots read `open` directly; uncontrolled roots
* seed from `defaultOpen` and track `onOpenChange`.
*
* The popup stays mounted through its exit slide. Consumers that key their
* content on a record or id (`{record && <Content />}`, a query with
* `skip: !id`) must keep that value alive until `onOpenChangeComplete(false)`
* fires; clearing it inside `onOpenChange` empties the panel while it is
* still visible.
*
* @param props - Drawer state, side, modal behavior, and open/close callbacks
*/
function Drawer({ side: sideProp, swipeDirection, open, defaultOpen, onOpenChange, snapPoints, defaultSnapPoint, ...props }) {
	const parentSide = React.useContext(DrawerSideContext);
	const side = useResolvedDrawerSide(sideProp ?? parentSide ?? DRAWER_SIDE_SHEET_ON_MOBILE);
	const [headerSnap, setHeaderSnap] = React.useState(null);
	const sheetSnapPoints = snapPoints ?? (side === "bottom" && headerSnap != null ? [headerSnap, 1] : void 0);
	const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
	const isOpen = open ?? uncontrolledOpen;
	const handleOpenChange = React.useCallback((nextOpen, eventDetails) => {
		setUncontrolledOpen(nextOpen);
		onOpenChange?.(nextOpen, eventDetails);
	}, [onOpenChange]);
	return /* @__PURE__ */ jsx(DrawerSideContext.Provider, {
		value: side,
		children: /* @__PURE__ */ jsx(DrawerOpenContext.Provider, {
			value: isOpen,
			children: /* @__PURE__ */ jsx(DrawerHeaderSnapContext.Provider, {
				value: setHeaderSnap,
				children: /* @__PURE__ */ jsx(Drawer$1.Root, {
					swipeDirection: swipeDirection ?? SIDE_TO_SWIPE_DIRECTION[side],
					open,
					defaultOpen,
					onOpenChange: handleOpenChange,
					snapPoints: sheetSnapPoints,
					defaultSnapPoint: defaultSnapPoint ?? (sheetSnapPoints && snapPoints == null ? 1 : void 0),
					...props
				})
			})
		})
	});
}
/**
* Renders the element that opens the drawer on interaction.
*
* @param props - Trigger element props forwarded to Base UI Drawer.Trigger
* @returns The rendered trigger element
*/
function DrawerTrigger(props) {
	return /* @__PURE__ */ jsx(Drawer$1.Trigger, { ...props });
}
/**
* Portals drawer content to the end of the document body, avoiding stacking
* context issues.
*
* @param props - Portal configuration forwarded to Base UI Drawer.Portal
*/
function DrawerPortal(props) {
	return /* @__PURE__ */ jsx(Drawer$1.Portal, { ...props });
}
/**
* Semi-transparent backdrop rendered behind the drawer popup.
*
* Opacity is tied to `--drawer-swipe-progress` so the backdrop fades as the
* user swipes to dismiss. Uses `data-[starting-style]` / `data-[ending-style]`
* for enter/exit transitions.
*
* @param props - Overlay props including optional className overrides
* @returns The rendered overlay element
*/
function DrawerOverlay({ className, ...props }) {
	return /* @__PURE__ */ jsx(Drawer$1.Backdrop, {
		className: cn("pointer-events-auto fixed inset-0 z-[100030] bg-black", "opacity-[calc(0.5*(1-var(--drawer-swipe-progress)))]", "transition-opacity duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)]", "data-[swiping]:duration-0", "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0", className),
		...props
	});
}
/**
* Popup panel styles.
*
* The resting transform combines Base UI's swipe offset with the stack
* recede: every open descendant drawer (`--drawer-stack-depth`) shifts the
* panel `--drawer-stack-offset` away from its anchored edge and shrinks it by
* `--drawer-stack-scale-step`, so a parent drawer reads as a card sitting
* behind the child. The scale origin sits on the edge facing the viewport
* centre (e.g. `origin-left` for a right drawer) so the shrink doesn't pull
* that edge back and cancel the shift — the full offset stays visible as a
* sliver beside the child. The transform strings are written out per side
* (rather than composed) so Tailwind's scanner can see the literal class names.
*/
const drawerPopupVariants = cva([
	"pointer-events-auto fixed z-[100031] box-border flex min-w-0 flex-col overflow-hidden font-body",
	"bg-elevation-surface-overlay shadow-elevation-overlay",
	"outline-none",
	"[--drawer-stack-offset:var(--spacing-lg)] [--drawer-stack-scale-step:0.04]",
	"transition-transform duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)]",
	"data-[swiping]:transition-none data-[swiping]:select-none",
	"after:pointer-events-none after:absolute after:inset-0 after:z-10 after:bg-black/0 after:transition-colors after:duration-150"
], {
	compoundVariants: [
		{
			className: "border-l border-l-(length:--border-width-default) border-l-border-primary",
			side: "right",
			variant: "attached"
		},
		{
			className: "border-r border-r-(length:--border-width-default) border-r-border-primary",
			side: "left",
			variant: "attached"
		},
		{
			className: "border-t border-t-(length:--border-width-default) border-t-border-primary",
			side: "bottom",
			variant: "attached"
		},
		{
			className: "border-b border-b-(length:--border-width-default) border-b-border-primary",
			side: "top",
			variant: "attached"
		}
	],
	defaultVariants: {
		side: "right",
		variant: "floating"
	},
	variants: {
		side: {
			right: [
				"inset-y-(--drawer-inset) right-(--drawer-inset) w-[400px] max-w-[calc(100vw-3rem)] origin-left",
				"[transform:translateX(calc(var(--drawer-swipe-movement-x,0px)-var(--drawer-stack-depth,0)*var(--drawer-stack-offset)))_scale(calc(1-var(--drawer-stack-depth,0)*var(--drawer-stack-scale-step)))]",
				"data-[starting-style]:[transform:translateX(calc(100%+var(--drawer-inset)))]",
				"data-[ending-style]:[transform:translateX(calc(100%+var(--drawer-inset)))]"
			],
			left: [
				"inset-y-(--drawer-inset) left-(--drawer-inset) w-[400px] max-w-[calc(100vw-3rem)] origin-right",
				"[transform:translateX(calc(var(--drawer-swipe-movement-x,0px)+var(--drawer-stack-depth,0)*var(--drawer-stack-offset)))_scale(calc(1-var(--drawer-stack-depth,0)*var(--drawer-stack-scale-step)))]",
				"data-[starting-style]:[transform:translateX(calc(-100%-var(--drawer-inset)))]",
				"data-[ending-style]:[transform:translateX(calc(-100%-var(--drawer-inset)))]"
			],
			bottom: [
				"[--drawer-bottom-offset:calc(var(--drawer-inset)+env(safe-area-inset-bottom,0px))]",
				"inset-x-(--drawer-inset) bottom-(--drawer-bottom-offset) max-h-[85dvh] origin-top",
				"[transform:translateY(calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y,0px)-var(--drawer-stack-depth,0)*var(--drawer-stack-offset)))_scale(calc(1-var(--drawer-stack-depth,0)*var(--drawer-stack-scale-step)))]",
				"data-[starting-style]:[transform:translateY(calc(100%+var(--drawer-bottom-offset)))]",
				"data-[ending-style]:[transform:translateY(calc(100%+var(--drawer-bottom-offset)))]"
			],
			top: [
				"inset-x-(--drawer-inset) top-(--drawer-inset) max-h-[85dvh] origin-bottom",
				"[transform:translateY(calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y,0px)+var(--drawer-stack-depth,0)*var(--drawer-stack-offset)))_scale(calc(1-var(--drawer-stack-depth,0)*var(--drawer-stack-scale-step)))]",
				"data-[starting-style]:[transform:translateY(calc(-100%-var(--drawer-inset)))]",
				"data-[ending-style]:[transform:translateY(calc(-100%-var(--drawer-inset)))]"
			]
		},
		variant: {
			/**
			* Panel floats inside the viewport: the same `--spacing-sm` (12px)
			* inset on every edge, rounded on all corners, and bordered on all
			* sides so it reads as a raised card. The width utility is the token
			* only; a bare `border` is 1px and made one edge measure wider.
			*/
			floating: "[--drawer-inset:var(--spacing-sm)] rounded-sm-m border-(length:--border-width-default) border-solid border-border-primary",
			/** Legacy flush panel pinned to the viewport edge with a single-edge border. */
			attached: "[--drawer-inset:0px]"
		}
	}
});
/**
* Grab pill shown on sheet-style drawers (`bottom` / `top`).
*
* Decorative only: Base UI listens for the dismiss swipe on the whole popup,
* so the pill does not need to be the gesture target. It is there so the
* gesture reads as available, which a bare card edge does not convey.
*
* @returns The rendered handle
*/
function DrawerHandle() {
	return /* @__PURE__ */ jsx("div", {
		"aria-hidden": true,
		"data-drawer-handle": "",
		className: "mx-auto mt-sm mb-sm h-xxs w-xl shrink-0 rounded-round bg-border-bold"
	});
}
/**
* Curri-styled drawer popup panel built on Base UI primitives.
*
* Renders inside a portal with an optional backdrop overlay. The Popup is
* fixed-positioned at the edge determined by the parent `Drawer`'s `side`
* context. The default `floating` variant insets the panel from every
* viewport edge (`--drawer-inset`) with rounded corners and a full border so
* it reads as a raised card; `attached` restores the flush, edge-pinned panel.
* Both variants slide in from their anchored edge — the enter/exit transform
* travels `100% + --drawer-inset` so a floating panel clears the viewport
* completely.
*
* Uses `border-box` sizing so padded header and footer sections stay within
* the panel width, and `overflow-hidden` to contain content within the panel.
* Drawers participate in the shared overlay stack, so dialogs and alert
* dialogs opened from within a drawer can layer above it cleanly. Nested child
* overlays register through `OverlaySurfaceContext`, which tints the drawer
* like a parent dialog would. Nested *drawers* additionally register through
* `DrawerStackContext`: the parent exposes `--drawer-stack-depth` (number of
* open descendant drawers) plus a `data-stacked` attribute, and recedes —
* scaling down and shifting away from its anchored edge — so a stack of
* drawers reads as layered cards. Base UI's
* `data-[starting-style]` / `data-[ending-style]` attributes drive the slide
* transitions.
*
* `bottom` and `top` render as sheets: they span the viewport width (any
* `w-*` / `max-w-*` in `className` is dropped, since those size side panels)
* and show a drag handle on the edge facing the viewport centre so the
* swipe-to-dismiss gesture Base UI already wires is discoverable. Pass a
* responsive `side` to the parent `Drawer` to switch a side panel to a sheet
* below a breakpoint.
*
* @param props - Popup configuration, variant, overlay toggle, and class overrides
* @returns The rendered drawer content panel
*/
function DrawerContent({ className, variant, showOverlay, overlayClassName, children, style, ...props }) {
	const side = React.useContext(DrawerSideContext) ?? "right";
	const isSheet = side === "bottom" || side === "top";
	const reportHeaderSnap = React.useContext(DrawerHeaderSnapContext);
	const [popupElement, setPopupElement] = React.useState(null);
	React.useLayoutEffect(() => {
		if (!reportHeaderSnap) return;
		if (side !== "bottom" || !popupElement) {
			reportHeaderSnap(null);
			return;
		}
		const measure = () => {
			const header = popupElement.querySelector("[data-drawer-header]");
			const handle = popupElement.querySelector("[data-drawer-handle]");
			const height = (header instanceof HTMLElement ? header.offsetHeight : 0) + (handle instanceof HTMLElement ? handle.offsetHeight : 0);
			reportHeaderSnap(height > 0 ? Math.ceil(height) : null);
		};
		measure();
		if (typeof ResizeObserver !== "function") return;
		const observer = new ResizeObserver(measure);
		for (const selector of ["[data-drawer-header]", "[data-drawer-handle]"]) {
			const node = popupElement.querySelector(selector);
			if (node) observer.observe(node);
		}
		return () => observer.disconnect();
	}, [
		popupElement,
		reportHeaderSnap,
		side
	]);
	const overlayDepth = React.useContext(OverlayStackContext);
	const isDrawerOpen = React.useContext(DrawerOpenContext);
	const { hasNestedOverlay, registerNestedOverlay } = useOverlaySurfaceState(isDrawerOpen);
	const { nestedDrawerCount, registerNestedDrawer } = useDrawerStackState(isDrawerOpen);
	const isNestedOverlay = overlayDepth > 0;
	const { overlay: zOverlay, popup: zPopup } = getOverlayLayerZIndex(overlayDepth);
	return /* @__PURE__ */ jsxs(DrawerPortal, { children: [showOverlay !== false && /* @__PURE__ */ jsx(DrawerOverlay, {
		className: cn(isNestedOverlay && showOverlay === void 0 && "bg-transparent", overlayClassName),
		style: { zIndex: zOverlay },
		forceRender: isNestedOverlay || void 0
	}), /* @__PURE__ */ jsx(Drawer$1.Viewport, {
		className: "pointer-events-none fixed inset-0",
		style: { zIndex: zPopup },
		children: /* @__PURE__ */ jsxs(Drawer$1.Popup, {
			ref: setPopupElement,
			className: cn(drawerPopupVariants({
				side,
				variant
			}), hasNestedOverlay && "after:bg-black/20", className, isSheet && cn("w-auto max-w-[unset] !grid", side === "bottom" ? "grid-rows-[auto_minmax(0,1fr)]" : "grid-rows-[minmax(0,1fr)_auto]")),
			style: (state) => {
				return {
					...typeof style === "function" ? style(state) : style,
					zIndex: zPopup,
					"--drawer-stack-depth": nestedDrawerCount
				};
			},
			"data-stacked": nestedDrawerCount > 0 ? "" : void 0,
			...props,
			children: [
				side === "bottom" && /* @__PURE__ */ jsx(DrawerHandle, {}),
				/* @__PURE__ */ jsx(OverlayStackContext.Provider, {
					value: overlayDepth + 1,
					children: /* @__PURE__ */ jsx(OverlaySurfaceContext.Provider, {
						value: registerNestedOverlay,
						children: /* @__PURE__ */ jsx(DrawerStackContext.Provider, {
							value: registerNestedDrawer,
							children: /* @__PURE__ */ jsx("div", {
								className: cn(isSheet ? "grid min-h-0 grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden" : "contents"),
								children
							})
						})
					})
				}),
				side === "top" && /* @__PURE__ */ jsx(DrawerHandle, {})
			]
		})
	})] });
}
/**
* Semantic wrapper for the drawer title and description area.
* Stacks children vertically with a 12 px gap inside uniform `md` padding,
* so the description never sits directly on the separator. Separated from
* the body by a bottom border, mirroring `DrawerFooter`'s top border.
*
* @param props - Standard div props
* @returns The rendered header container
*/
function DrawerHeader({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-drawer-header": "",
		className: cn("box-border flex w-full min-w-0 shrink-0 flex-col gap-sm border-b border-b-(length:--border-width-default) border-b-border-primary p-md", className),
		...props
	});
}
/**
* Bottom-pinned container for drawer action buttons.
* Lays out children in a horizontal row with space-between alignment,
* separated from the body by a top border.
*
* @param props - Standard div props
* @returns The rendered footer container
*/
function DrawerFooter({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("box-border flex w-full min-w-0 shrink-0 items-center justify-between border-t border-t-(length:--border-width-default) border-t-border-primary p-md", className),
		...props
	});
}
/**
* Renders the drawer heading using the Base UI Drawer.Title primitive for
* proper `aria-labelledby` wiring.
*
* Styled as the Figma "heading/medium" treatment: 18 px semibold with tight
* letter-spacing.
*
* @param props - Title props forwarded to Base UI Drawer.Title
* @returns The rendered title element
*/
function DrawerTitle({ className, ...props }) {
	return /* @__PURE__ */ jsx(Drawer$1.Title, {
		className: cn("m-0 font-body text-heading-medium leading-heading-medium font-heading-semi-bold tracking-xs text-text-primary", className),
		...props
	});
}
/**
* Renders the drawer body text using the Base UI Drawer.Description primitive
* for proper `aria-describedby` wiring.
*
* Styled with the Figma "body/medium" treatment: 12 px medium weight,
* secondary text color.
*
* @param props - Description props forwarded to Base UI Drawer.Description
* @returns The rendered description element
*/
function DrawerDescription({ className, ...props }) {
	return /* @__PURE__ */ jsx(Drawer$1.Description, {
		className: cn("m-0 font-body text-body-medium leading-body-medium font-heading-medium tracking-sm text-text-secondary", className),
		...props
	});
}
/**
* Renders a close trigger that dismisses the drawer.
* Delegates to Base UI Drawer.Close.
*
* @param props - Close trigger props forwarded to Base UI Drawer.Close
* @returns The rendered close trigger
*/
function DrawerClose(props) {
	return /* @__PURE__ */ jsx(Drawer$1.Close, { ...props });
}
/**
* Pre-styled close icon button for the drawer heading dock.
*
* Renders a Phosphor X icon inside a Base UI Drawer.Close primitive with
* hover and focus-visible treatments.
*
* @param props - Close button configuration and optional icon size
* @returns The rendered icon close button
*/
function DrawerCloseButton({ className, iconSize = 12, ...props }) {
	return /* @__PURE__ */ jsx(Drawer$1.Close, {
		className: cn("inline-flex shrink-0 cursor-pointer items-center justify-center rounded-xs border-0 bg-background-neutral-secondary p-button-height text-text-secondary outline-none", "hover:bg-background-neutral-secondary-hover hover:text-text-primary", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-text-selected", className),
		...props,
		children: /* @__PURE__ */ jsx(XIcon, { style: {
			width: iconSize,
			height: iconSize
		} })
	});
}
/**
* Horizontal layout container for the drawer title and close button.
* Uses space-between to push the close icon to the trailing edge,
* matching the Figma heading dock pattern.
*
* Children align to the top of the row rather than its centre. The close
* button belongs to the panel corner, so when a title wraps (a two-line route
* such as "123 Main St → / 456 Oak Ave") the X stays beside the first line
* instead of drifting down to the midpoint. A single-line title renders the
* same either way.
*
* @param props - Standard div props
* @returns The rendered heading dock row
*/
function DrawerHeadingDock({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("box-border flex w-full min-w-0 items-start justify-between", className),
		...props
	});
}
/**
* Scrollable main content area between the drawer header and footer.
*
* Fills the remaining vertical space and renders an internal ScrollArea so
* long content can scroll independently while the header and footer remain
* pinned. Standard div props are forwarded to the padded content wrapper,
* which keeps `box-border` sizing so the drawer's inset spacing still aligns
* cleanly with the panel border treatment.
*
* @param props - Standard div props
* @returns The rendered body container
*/
function DrawerBody({ className, children, ...props }) {
	return /* @__PURE__ */ jsx(Drawer$1.Content, {
		className: "group/drawer-body box-border flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden last:pb-md",
		children: /* @__PURE__ */ jsxs(ScrollArea, {
			className: "h-full w-full overflow-hidden [--scroll-fade-color:var(--color-elevation-surface-overlay)]",
			children: [/* @__PURE__ */ jsx(ScrollAreaViewport, {
				className: "h-full w-full !overflow-x-hidden",
				scrollFade: true,
				children: /* @__PURE__ */ jsx(ScrollAreaContent, {
					className: cn("box-border flex min-h-full !min-w-0 max-w-full w-full flex-col overflow-x-hidden p-md group-last/drawer-body:pb-0", className),
					...props,
					children
				})
			}), /* @__PURE__ */ jsx(ScrollAreaScrollbar, { children: /* @__PURE__ */ jsx(ScrollAreaThumb, {}) })]
		})
	});
}
//#endregion
export { DRAWER_SIDE_SHEET_ON_MOBILE, Drawer, DrawerBody, DrawerClose, DrawerCloseButton, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerHeadingDock, DrawerOverlay, DrawerPortal, DrawerTitle, DrawerTrigger };

//# sourceMappingURL=Drawer.js.map